import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import Documents from '../homepage/Documents';
import InfoMessages from '../homepage/InfoMessages';
import moment from 'moment';
import i18n from '../../language/i18n';
import { MARKET } from '../../constants/storageKeys';
import { defaultLangPerMarket } from '../../constants/flags';
import Spinner from '../Controls/Spinner';
import { useInfoMessages, useAdminInfoMessages, InfoMessage } from '../../hooks/useInfoMessages';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import { isRecentlyUpdated } from '../../constants/recentlyUpdated';

interface IProps {
    isAdminPage?: boolean;
}

function InfoMessagesList({ isAdminPage }: IProps) {
    const history = useHistory();
    const market = window.localStorage.getItem(MARKET) ?? 'se';
    const lang = defaultLangPerMarket[market];

    const { data: publicMessages = [], isLoading: isLoadingPublic } = useInfoMessages(lang, { enabled: !isAdminPage });
    const { data: adminMessages = [], isLoading: isLoadingAdmin } = useAdminInfoMessages({ enabled: isAdminPage });

    const isLoading = isAdminPage ? isLoadingAdmin : isLoadingPublic;
    const messages = isAdminPage ? adminMessages : publicMessages;

    function openInfoMessage(id: number) {
        history.push('/infomessage/' + id);
    }

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: isAdminPage ? 12 : 8 }}>
                {isLoading ? <Spinner /> :
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h5" fontWeight={600}>
                                {i18n.t('news.header')}
                            </Typography>
                            {isAdminPage && (
                                <Button
                                    component={Link}
                                    to={'/administrationinfomessage/0'}
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    size="small"
                                >
                                    {i18n.t('newsedit.add')}
                                </Button>
                            )}
                        </Box>
                        <TableContainer component={Paper} elevation={0} className="homePageTable" sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'background.paper' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {isAdminPage ? i18n.t('news.fromdate') : i18n.t('news.date')}
                                        </TableCell>
                                        {isAdminPage && <TableCell sx={{ fontWeight: 600 }}>{i18n.t('news.todate')}</TableCell>}
                                        <TableCell sx={{ fontWeight: 600 }}>{i18n.t('news.news')}</TableCell>
                                        {isAdminPage && <TableCell sx={{ width: 60 }} />}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isAdminPage
                                        ? messages.map((info: InfoMessage) =>
                                            <TableRow key={info.infoMessageId} hover>
                                                <TableCell>{moment(info.showFromDateUTC).format('L')}</TableCell>
                                                <TableCell>{moment(info.showToDateUTC).format('L')}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {info.title}
                                                        {isRecentlyUpdated(info.updateDateUTC) && (
                                                            <Chip size="small" label="Ny" data-testid="recently-updated" color="success"
                                                                sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        component={Link}
                                                        to={'/administrationinfomessage/' + info.infoMessageId}
                                                        size="small"
                                                        color="primary"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                        : messages.map((info: InfoMessage) =>
                                            <TableRow key={info.infoMessageId} hover sx={{ cursor: 'pointer' }}
                                                onClick={() => openInfoMessage(info.infoMessageId)}>
                                                <TableCell>{moment(info.showFromDateUTC).format('L')}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography component="a" sx={{ color: 'secondary.main', cursor: 'pointer' }}>
                                                            {info.title}
                                                        </Typography>
                                                        {isRecentlyUpdated(info.updateDateUTC) && (
                                                            <Chip size="small" label="Ny" data-testid="recently-updated" color="success"
                                                                sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                }
            </Grid>

            {!isAdminPage && (
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Box>
                        <InfoMessages />
                    </Box>
                    <Box sx={{ mt: 9 }}>
                        <Documents />
                    </Box>
                </Grid>
            )}
        </Grid>
    );
}

export default InfoMessagesList;
